import type { ReactNode } from 'react';
import React, {
    createContext,
    useCallback,
    useContext,
    useState,
} from 'react';

export interface StepHookCallbacks {
    onNext?: () => void;
    onPrevious?: () => void;
    onComplete?: () => void;
    nextLabel?: string;
    backLabel?: string;
    disableNext?: boolean;
    disableBack?: boolean;
}

interface UiStepperContextType {
    currentStep: number;
    totalSteps: number;
    visitedSteps: Set<number>;
    nextStep: () => void;
    prevStep: () => void;
    goToStep: (step: number) => void;
    resetVisitedSteps: (step?: number) => void;
    canNavigateToStep: (step: number) => boolean;
    isFirstStep: boolean;
    isLastStep: boolean;
    stepHooks: StepHookCallbacks[];
    setStepHooks: (step: number, hooks: StepHookCallbacks) => void;
    isLoading: boolean;
    setNextLoading: (loading: boolean) => void;
    onSubmitHandler?: (data: any) => Promise<void>;
}

const defaultContextValue: UiStepperContextType = {
    currentStep: 0,
    totalSteps: 0,
    visitedSteps: new Set([ 0 ]),
    nextStep: () => {},
    prevStep: () => {},
    goToStep: () => {},
    resetVisitedSteps: () => {},
    canNavigateToStep: () => false,
    isFirstStep: true,
    isLastStep: true,
    stepHooks: [],
    setStepHooks: () => {},
    isLoading: false,
    setNextLoading: () => {},
    onSubmitHandler: undefined,
};

const UiStepperContext = createContext<UiStepperContextType>(defaultContextValue);

interface UiStepperProviderProps {
    children: ReactNode;
    totalSteps: number;
    onSubmitHandler?: (data: any) => Promise<void>;
    initialStep?: number;
}

export const UiStepperProvider: React.FC<UiStepperProviderProps> = ({
    children,
    totalSteps,
    onSubmitHandler,
    initialStep = 0,
}) => {
    // Initialize visitedSteps to include all steps from 0 to initialStep
    const initialVisitedSteps = React.useMemo(() => {
        const visited = new Set<number>();
        for (let i = 0; i <= Math.min(initialStep, totalSteps - 1); i++) {
            visited.add(i);
        }
        return visited;
    }, [ initialStep, totalSteps ]);

    const [ currentStep, setCurrentStep ] = useState(initialStep);
    const [ visitedSteps, setVisitedSteps ] = useState(initialVisitedSteps);
    const [ stepHooks, setStepHooksState ] = useState<StepHookCallbacks[]>(Array.from({ length: totalSteps }));
    const [ isLoading, setNextLoading ] = useState(false);

    const setStepHooks = useCallback((step: number, hooks: StepHookCallbacks) => {
        setStepHooksState((prev) => {
            const newHooks = [ ...prev ];
            newHooks[step] = hooks;
            return newHooks;
        });
    }, []);

    const nextStep = useCallback(() => {
        const currentStepHooks = stepHooks[currentStep];
        const isLastStep = currentStep === totalSteps - 1;

        if (isLastStep) {
            currentStepHooks?.onComplete?.();
            return;
        }

        // Check disableNext BEFORE calling onNext to prevent navigation early
        if (currentStepHooks?.disableNext) {
            // Still call onNext for validation/error handling, but don't navigate
            currentStepHooks?.onNext?.();
            return;
        }

        // Handle step navigation
        currentStepHooks?.onNext?.();
        setCurrentStep((prev) => {
            const nextStepIndex = Math.min(prev + 1, totalSteps - 1);
            setVisitedSteps((visited) => new Set([ ...visited, nextStepIndex ]));
            return nextStepIndex;
        });
    }, [ currentStep, stepHooks, totalSteps ]);

    const prevStep = useCallback(() => {
        const currentStepHooks = stepHooks[currentStep];

        // Call step's onPrevious hook if defined
        if (currentStepHooks?.onPrevious) {
            currentStepHooks.onPrevious();
        }

        // Navigate to previous step
        setCurrentStep((prev) => Math.max(prev - 1, 0));
    }, [ currentStep, stepHooks ]);

    const goToStep = useCallback((step: number) => {
        const targetStep = Math.max(0, Math.min(step, totalSteps - 1));
        setCurrentStep(targetStep);
        setVisitedSteps((visited) => new Set([ ...visited, targetStep ]));
    }, [ totalSteps ]);

    const resetVisitedSteps = useCallback((step: number = 0) => {
        const targetStep = Math.max(0, Math.min(step, totalSteps - 1));
        setCurrentStep(targetStep);
        setVisitedSteps(new Set([ targetStep ]));
    }, [ totalSteps ]);

    const canNavigateToStep = useCallback((step: number) => visitedSteps.has(step), [ visitedSteps ]);

    const isFirstStep = currentStep === 0;
    const isLastStep = currentStep === totalSteps - 1;

    const value: UiStepperContextType = {
        currentStep,
        totalSteps,
        visitedSteps,
        nextStep,
        prevStep,
        goToStep,
        resetVisitedSteps,
        canNavigateToStep,
        isFirstStep,
        isLastStep,
        stepHooks,
        setStepHooks,
        isLoading,
        setNextLoading,
        onSubmitHandler,
    };

    return (
        <UiStepperContext.Provider value={value}>
            {children}
        </UiStepperContext.Provider>
    );
};

export const useUiStepper = (): UiStepperContextType => useContext(UiStepperContext);
